import { Link, MessageBar, MessageBarType, Stack, mergeStyles } from "@fluentui/react";
import { Checkbox } from "@fluentui/react/lib/Checkbox";
import { Dropdown, IDropdownOption } from "@fluentui/react/lib/Dropdown";
import { Icon } from "@fluentui/react/lib/Icon";
import { TooltipHost } from "@fluentui/react/lib/Tooltip";
import { useTheme } from "cayo.ui";
import React, { useCallback, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router";
import styled from "styled-components";
import { lookupIcon } from "../../../components/GridContainer/renderers/common";
import ajax from "@cayo/shared/libs/ajax";
import useTypeAnnotations from "../../GlobalHooks/type-annotations.hook";
import { wizardMessages } from "../messages";
import { InitialStateType } from "../types";

const rootStyle = mergeStyles({
  "::after": {
    inset: "-20px",
  },
});

type ADConnectorProps = {
  state: InitialStateType;
  agents: any[];
  firstAd: any[];
  adChecked: boolean;
  isHosted: boolean;
  isInitial: boolean;
  setAdChecked: (props: boolean) => void;
  dispatch: (data: any) => void;
  onClose: (result: boolean) => void;
};

const ADConnector = ({
  state,
  agents,
  firstAd,
  adChecked,
  isHosted,
  isInitial,
  setAdChecked,
  onClose,
  dispatch,
}: ADConnectorProps) => {
  const { data: propertyAnnotations } = ajax.usePropertyAnnotations(`v0/{schemeId}`, {
    schemeId: "configuration/workerSystemNodes",
  });
  const typeAnnotations = useTypeAnnotations();
  const theme = useTheme();
  const intl = useIntl();
  const history = useHistory();
  const { agentId } = state?.credentials;
  const cayoTheme = theme.cayoTheme;

  const agentsList =
    [...firstAd, ...agents]?.map((item: any) => ({
      key: item.id,
      text: item.name,
      objectPath: item.objectPath,
      data: lookupIcon(
        item,
        {
          intl,
          theme,
          getTypeAnnotation: typeAnnotations.getTypeAnnotation,
          getIconAnnotation: typeAnnotations.getIconAnnotation,
          propertyAnnotations,
        },
        "compoundStatus"
      ),
    })) || [];

  useEffect(() => {
    if (agentsList.length && !agentId && adChecked) {
      updateAgentState(agentsList[0]);
    }
  }, [agentId, adChecked, agentsList.length]);

  const updateAgentState = useCallback((agent: any) => {
    const agentId = agent ? agent.key : null;

    dispatch({
      type: "update",
      payload: {
        form: "credentials",
        data: {
          agentId,
        },
      },
    });

    dispatch({
      type: "update",
      payload: {
        form: "creationForm",
        data: {
          node: agent ? { objectPath: agent.objectPath!, id: agent.key } : null,
        },
      },
    });
  }, []);

  return (
    <Wrapper>
      <MessageBar
        messageBarType={MessageBarType.info}
        truncated={true}
        styles={{
          root: {
            marginBottom: 26,
            background: cayoTheme.wizard.colors.active,
          },
        }}
      >
        {intl.formatMessage(
          isHosted ? wizardMessages.adConnectorMessageHosted : wizardMessages.adConnectorMessage,
          {
            link: (
              <a
                href={"https://support.cayosoft.com/hc/en-us/articles/36184031651725"}
                target="_blank"
                rel="noreferrer"
              >
                <FormattedMessage
                  id={wizardMessages.LearnMore.id}
                  defaultMessage={wizardMessages.LearnMore.defaultMessage}
                />
              </a>
            ),
          }
        )}
      </MessageBar>
      {!isHosted && (
        <Record>
          <Tooltip>
            <Checkbox
              required
              checked={adChecked}
              id="Enable AD"
              label={intl.formatMessage(wizardMessages.adConnectorCheckBox)}
              onChange={(_, checked) => {
                setAdChecked(checked || false);
                updateAgentState(null);
              }}
            />
            <IconWithTooltip
              tooltipText={intl.formatMessage(wizardMessages.adConnectorCheckBoxTooltip, {
                link: (
                  <a
                    href={"https://support.cayosoft.com/hc/en-us/articles/31204946876045"}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FormattedMessage
                      id={wizardMessages.LearnMore.id}
                      defaultMessage={wizardMessages.LearnMore.defaultMessage}
                    />
                  </a>
                ),
              })}
            />
          </Tooltip>
        </Record>
      )}

      <AgentWrap>
        <Dropdown
          disabled={!adChecked}
          placeholder={intl.formatMessage(wizardMessages.adConnectorPlaceholder)}
          label={intl.formatMessage(wizardMessages.adConnector)}
          options={agentsList}
          selectedKey={agentId}
          required
          onChange={(_: any, item: any) => {
            updateAgentState(item);
          }}
          onRenderOption={onRenderOption}
          onRenderTitle={onRenderTitle}
        />
      </AgentWrap>

      {!isInitial && (
        <LinkStyled
          onClick={() => {
            onClose && onClose(false);
            history.push({ pathname: "/changeAudit/agents" });
          }}
        >
          {intl.formatMessage(wizardMessages.goToAd)}
          <Icon iconName="ChromeBackMirrored" />
        </LinkStyled>
      )}
    </Wrapper>
  );
};

const onRenderTitle = (options?: IDropdownOption[]): JSX.Element | null => {
  if (!options || options.length === 0) {
    return null;
  }
  const option = options[0];
  return onRenderOption(option);
};

const onRenderOption = (option?: IDropdownOption): JSX.Element | null => {
  if (!option) {
    return null;
  }
  return (
    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
      {option.data && (
        <>
          <Icon iconName={option.data.iconId} styles={{ root: { ...option.data.iconStyle } }} />{" "}
          &nbsp;
        </>
      )}
      <span>{option.text}</span>
      {option.data && (
        <AgentStatus>
          &nbsp;{"("}
          <span>{option.data.title}</span>
          &nbsp;{")"}&nbsp;
        </AgentStatus>
      )}
    </Stack>
  );
};

const IconWithTooltip = ({ tooltipText }: { tooltipText: any }) => (
  <>
    <TooltipHost
      content={tooltipText}
      calloutProps={{
        styles: {
          root: rootStyle,
        },
      }}
      id={"adtooltip"}
    >
      <Icon iconName={"Info"} aria-describedby={"adicon"} />
    </TooltipHost>
  </>
);

const LinkStyled = styled(Link)`
  margin-top: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Tooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Record = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  font-weight: 500;
  margin-bottom: 20px;
`;

const AgentWrap = styled.div`
  width: 100%;
`;

const AgentStatus = styled.span`
  font-weight: 500;
`;

export default ADConnector;
